import { useCallback, useEffect, useState } from 'react';
import { TbAlertTriangle, TbCircleCheck, TbClockHour4, TbHelpCircle } from 'react-icons/tb';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { PageState } from '../components/PageState';
import { StatusBadge } from '../components/StatusBadge';
import { formatDate, formatMoney, PAYMENT_STATUS_LABELS } from '../lib/format';
import { memberApi as defaultMemberApi } from './member-api';
import './member.css';

const PAYMENT_METHOD_LABELS = { cash: 'Efectivo', transfer: 'Transferencia', card: 'Tarjeta', other: 'Otro' };
const PAYMENT_STATUS_PRESENTATION = {
  confirmed: { icon: TbCircleCheck, tone: 'success' },
  pending: { icon: TbClockHour4, tone: 'warning' },
  rejected: { icon: TbAlertTriangle, tone: 'danger' },
};

/**
 * `/mi-cuenta/cuota` (spec "Alumno"): the member's full period history from
 * the SAME `GET /members/:id` response as `MemberHomePage`, ordered exactly
 * as the API sends it (`startDate DESC`, each period's own `payments`
 * `paidOn ASC, createdAt ASC`) — never resorted client-side. Amount, paid,
 * balance and status are the server's own DTO fields (spec: "el cliente
 * nunca recalcula saldos, estados ni fechas de período").
 */
export function MemberMembershipsPage({ api = defaultMemberApi }) {
  const { user, handleApiError } = useAuth();
  const [state, setState] = useState({ status: 'loading', memberships: null, relevantMembershipId: null, error: '' });

  const load = useCallback(async () => {
    if (!user) return;
    setState({ status: 'loading', memberships: null, relevantMembershipId: null, error: '' });
    try {
      const data = await api.getOwnMember(user.id);
      setState({ status: 'loaded', memberships: data.memberships || [], relevantMembershipId: data.relevantMembershipId || null, error: '' });
    } catch (caught) {
      const outcome = handleApiError(caught, {
        clearBusinessData: () => setState({ status: 'loading', memberships: null, error: '' }),
      });
      if (outcome.handled) return;
      setState({ status: 'error', memberships: null, error: errorMessageFor(caught) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, user && user.id, handleApiError]);

  useEffect(() => {
    load();
  }, [load]);

  if (!user) return null;

  if (state.status === 'loading') {
    return <PageState title="Mi cuota" status="loading" />;
  }

  if (state.status === 'error') {
    return (
      <PageState
        title="Mi cuota"
        status="error"
        message={state.error}
        action={<button type="button" className="gym-button gym-button--secondary" onClick={load}>Reintentar</button>}
      />
    );
  }

  if (state.memberships.length === 0) {
    return <PageState title="Mi cuota" status="empty" message="Todavía no tenés cuotas registradas." />;
  }

  return (
    <section className="gym-page" aria-labelledby="gym-page-title">
      <header className="gym-page__header">
        <h1 id="gym-page-title" className="gym-page__title">Mi cuota</h1>
      </header>

      <table className="gym-table gym-periods-table">
        <caption className="gym-sr-only">Historial de cuotas</caption>
        <thead>
          <tr>
            <th scope="col">Plan</th>
            <th scope="col">Período</th>
            <th scope="col">Importe</th>
            <th scope="col">Pagado</th>
            <th scope="col">Saldo</th>
            <th scope="col">Estado</th>
            <th scope="col">Pagos</th>
          </tr>
        </thead>
        <tbody>
          {state.memberships.map(membership => (
            <tr key={membership.id} className={membership.id === state.relevantMembershipId ? 'is-current' : undefined}>
              <td data-label="Plan">{membership.planName}</td>
              <td data-label="Período">{formatDate(membership.startDate)} – {formatDate(membership.endDate)}</td>
              <td data-label="Importe">{formatMoney(membership.amount)}</td>
              <td data-label="Pagado">{formatMoney(membership.paid)}</td>
              <td data-label="Saldo">{formatMoney(membership.balance)}</td>
              <td data-label="Estado"><StatusBadge status={membership.status} /></td>
              <td data-label="Pagos">
                {membership.payments && membership.payments.length > 0 ? (
                  <ul className="gym-payment-list">
                    {membership.payments.map(payment => (
                      <li key={payment.id} className="gym-period-payment__item">
                        <span className="gym-period-payment__details">{formatDate(payment.paidOn)} · {PAYMENT_METHOD_LABELS[payment.method] || payment.method}</span>
                        <span className="gym-period-payment__amount">{formatMoney(payment.amount)}</span>
                        <span className="gym-period-payment__label">Estado</span>
                        <PaymentStatus status={payment.status} />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="gym-muted-text">Sin pagos</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function PaymentStatus({ status }) {
  const presentation = PAYMENT_STATUS_PRESENTATION[status] || { icon: TbHelpCircle, tone: 'neutral' };
  const Icon = presentation.icon;
  return (
    <span className={`gym-period-payment__status gym-period-payment__status--${presentation.tone}`}>
      <Icon aria-hidden="true" focusable="false" />
      {PAYMENT_STATUS_LABELS[status] || status}
    </span>
  );
}
